/* eslint-disable react/prop-types */
import { useCallback, useEffect, useRef, useState } from "react";
import axios from "axios";
import {
    AlertTriangle, CheckCircle2, ChevronDown, Info, RefreshCw, Smartphone,
} from "lucide-react";
import { BrandButton, CopyableField, StatusBadge, TextInput } from "../../../../components/ui/index.js";
import { paymentApiError, SPANISH_PHONE_RE } from "../../../../components/store/paymentStatus.js";

export const BIZUM_CONFIG_ANCHOR = "config-cobro-bizum";

/**
 * BizumReceiverCard — configuración del receptor Bizum desde la aplicación.
 *
 * Es la vía operativa oficial: publica una versión nueva mediante
 * `POST /payments-admin/receiver/`, que desactiva la anterior y deja auditoría.
 * El admin de Django sólo permite consultar el historial.
 *
 * Publicar un receptor NO repara pagos ya emitidos sin snapshot: eso es una
 * acción explícita por pedido desde el panel de revisión.
 */
export default function BizumReceiverCard({ onReceiverPublished }) {
    const [receiver, setReceiver] = useState(null);
    const [loading, setLoading] = useState(true);
    const [loadError, setLoadError] = useState("");
    const [open, setOpen] = useState(false);

    const [form, setForm] = useState({ receiver_name: "", receiver_phone: "", notes: "" });
    const [touched, setTouched] = useState(false);
    const [saving, setSaving] = useState(false);
    const [saveError, setSaveError] = useState("");
    const [savedVersion, setSavedVersion] = useState(null);

    const savingRef = useRef(false);

    const load = useCallback(async () => {
        setLoading(true);
        setLoadError("");
        try {
            const { data } = await axios.get("/payments-admin/receiver/");
            setReceiver(data.receiver ?? null);
            // Sin receptor configurado no se puede cobrar: se abre solo.
            if (!data.receiver) setOpen(true);
        } catch (err) {
            setLoadError(paymentApiError(err, "No se ha podido cargar la configuración de cobro."));
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => { load(); }, [load]);

    const phoneValid = SPANISH_PHONE_RE.test(form.receiver_phone);
    const nameValid = form.receiver_name.trim().length > 0;
    const showPhoneError = touched && form.receiver_phone.length > 0 && !phoneValid;
    const canSave = nameValid && phoneValid && !saving;

    const setField = (name, value) => setForm((cur) => ({ ...cur, [name]: value }));

    const handleSubmit = async (event) => {
        event.preventDefault();
        setTouched(true);
        if (!canSave || savingRef.current) return;   // corta el doble envío

        savingRef.current = true;
        setSaving(true);
        setSaveError("");
        setSavedVersion(null);
        try {
            const { data } = await axios.post("/payments-admin/receiver/", {
                receiver_name: form.receiver_name.trim(),
                receiver_phone: form.receiver_phone.trim(),
                notes: form.notes.trim(),
            });
            setReceiver(data.receiver);
            setSavedVersion(data.receiver?.version ?? null);
            setForm({ receiver_name: "", receiver_phone: "", notes: "" });
            setTouched(false);
            onReceiverPublished?.(data.receiver);
        } catch (err) {
            setSaveError(paymentApiError(err, "No se ha podido publicar el receptor."));
        } finally {
            savingRef.current = false;
            setSaving(false);
        }
    };

    return (
        <section
            id={BIZUM_CONFIG_ANCHOR}
            className="glassCard rounded-xl p-3 flex flex-col gap-3 scroll-mt-4"
            aria-labelledby="bizum-config-title"
        >
            <div className="flex items-center justify-between gap-3 flex-wrap">
                <div className="flex items-center gap-3 min-w-0">
                    <Smartphone className="w-5 h-5 text-primary shrink-0" aria-hidden />
                    <div className="min-w-0">
                        <h2 id="bizum-config-title" className="font-semibold">
                            Configuración de cobro Bizum
                        </h2>
                        {loading ? (
                            <div className="skeleton h-3 w-40 mt-1" />
                        ) : receiver ? (
                            <p className="text-xs text-base-content/60 truncate">
                                {receiver.receiver_name} · {receiver.receiver_phone} · v{receiver.version}
                            </p>
                        ) : (
                            <p className="text-xs text-warning">
                                Sin receptor configurado: los pedidos nuevos no muestran instrucciones.
                            </p>
                        )}
                    </div>
                </div>

                <div className="flex items-center gap-2">
                    {!loading && (
                        receiver
                            ? <StatusBadge
                                label={`Activo v${receiver.version}`}
                                tone="success"
                                icon={<CheckCircle2 className="w-3.5 h-3.5" />}
                              />
                            : <StatusBadge
                                label="Sin configurar"
                                tone="warning"
                                icon={<AlertTriangle className="w-3.5 h-3.5" />}
                              />
                    )}
                    <button
                        type="button"
                        className="btn btn-ghost btn-xs btn-square"
                        onClick={load}
                        aria-label="Actualizar la configuración de cobro"
                        title="Actualizar"
                    >
                        <RefreshCw className="w-3.5 h-3.5" aria-hidden />
                    </button>
                    <button
                        type="button"
                        className="btn btn-xs btn-outline gap-1"
                        aria-expanded={open}
                        aria-controls="bizum-config-body"
                        onClick={() => setOpen((v) => !v)}
                    >
                        {open ? "Ocultar" : "Configurar"}
                        <ChevronDown
                            className={`w-3 h-3 transition-transform ${open ? "rotate-180" : ""}`}
                            aria-hidden
                        />
                    </button>
                </div>
            </div>

            {loadError && (
                <div role="alert" className="alert alert-error py-2">
                    <AlertTriangle className="w-4 h-4" aria-hidden />
                    <span className="text-sm">{loadError}</span>
                    <button type="button" className="btn btn-sm btn-ghost gap-1" onClick={load}>
                        <RefreshCw className="w-4 h-4" aria-hidden /> Reintentar
                    </button>
                </div>
            )}

            {open && !loadError && (
                <div id="bizum-config-body" className="flex flex-col gap-3 border-t border-base-content/10 pt-3">
                    {loading ? (
                        <div className="flex flex-col gap-2" aria-busy="true">
                            <span className="sr-only">Cargando la configuración…</span>
                            <div className="skeleton h-4 w-32" />
                            <div className="skeleton h-10 w-full" />
                        </div>
                    ) : (
                        <>
                            {receiver ? (
                                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 rounded-lg bg-base-200/60 p-3">
                                    <CopyableField
                                        label="Nombre receptor"
                                        value={receiver.receiver_name}
                                        mono={false}
                                    />
                                    <CopyableField
                                        label="Teléfono receptor"
                                        value={receiver.receiver_phone}
                                        mono={false}
                                    />
                                    <div className="flex flex-col gap-1 min-w-0">
                                        <span className="text-xs uppercase tracking-wide font-semibold text-base-content/55">
                                            Versión
                                        </span>
                                        <span className="font-medium">v{receiver.version}</span>
                                        <span className="text-xs text-base-content/55">
                                            {receiver.created_at
                                                ? new Date(receiver.created_at).toLocaleString("es-ES")
                                                : ""}
                                        </span>
                                    </div>
                                    <div className="flex flex-col gap-1 min-w-0">
                                        <span className="text-xs uppercase tracking-wide font-semibold text-base-content/55">
                                            Notas
                                        </span>
                                        <span className={`text-sm ${receiver.notes ? "" : "text-base-content/40 italic"}`}>
                                            {receiver.notes || "Sin notas"}
                                        </span>
                                    </div>
                                </div>
                            ) : (
                                <div role="status" className="alert alert-warning py-2">
                                    <AlertTriangle className="w-4 h-4" aria-hidden />
                                    <span className="text-sm">
                                        Todavía no hay receptor. Hasta que publiques uno, los pedidos
                                        se crean sin instrucciones de pago.
                                    </span>
                                </div>
                            )}

                            {savedVersion !== null && (
                                <div role="status" className="alert alert-success py-2">
                                    <CheckCircle2 className="w-4 h-4" aria-hidden />
                                    <span className="text-sm">
                                        Receptor publicado como v{savedVersion}. Los pedidos nuevos
                                        ya lo usarán.
                                    </span>
                                </div>
                            )}

                            <form onSubmit={handleSubmit} className="flex flex-col gap-3" noValidate>
                                <h3 className="text-sm font-semibold">
                                    {receiver ? "Publicar una versión nueva" : "Publicar el receptor"}
                                </h3>

                                <div role="note" className="alert py-2">
                                    <Info className="w-4 h-4 shrink-0" aria-hidden />
                                    <span className="text-xs">
                                        Publicar una versión nueva desactiva la anterior y solo afecta a
                                        los <strong>pedidos nuevos</strong>. Los pagos ya emitidos
                                        conservan el receptor que vio el cliente; un pago que nació sin
                                        receptor se repara uno a uno desde su pedido.
                                    </span>
                                </div>

                                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                                    <div className="form-control gap-1">
                                        <label className="label py-0" htmlFor="bizum-receiver-name">
                                            <span className="label-text text-xs font-semibold">
                                                Nombre del receptor
                                            </span>
                                        </label>
                                        <TextInput
                                            id="bizum-receiver-name"
                                            name="receiver_name"
                                            inputSize="sm"
                                            autoComplete="off"
                                            placeholder="Nombre que verá el cliente"
                                            value={form.receiver_name}
                                            disabled={saving}
                                            aria-describedby="bizum-receiver-name-help"
                                            onChange={(e) => setField("receiver_name", e.target.value)}
                                        />
                                        <span id="bizum-receiver-name-help" className="label-text-alt text-base-content/55">
                                            Tal y como debe aparecerle al hacer el Bizum.
                                        </span>
                                    </div>

                                    <div className="form-control gap-1">
                                        <label className="label py-0" htmlFor="bizum-receiver-phone">
                                            <span className="label-text text-xs font-semibold">
                                                Teléfono receptor
                                            </span>
                                        </label>
                                        <TextInput
                                            id="bizum-receiver-phone"
                                            name="receiver_phone"
                                            type="tel"
                                            inputMode="tel"
                                            inputSize="sm"
                                            autoComplete="off"
                                            placeholder="600 12 34 56"
                                            value={form.receiver_phone}
                                            disabled={saving}
                                            variant={showPhoneError ? "error" : undefined}
                                            aria-invalid={showPhoneError || undefined}
                                            aria-describedby="bizum-receiver-phone-help"
                                            leftIcon={<Smartphone className="w-4 h-4" />}
                                            onChange={(e) => setField("receiver_phone", e.target.value)}
                                            onBlur={() => setTouched(true)}
                                        />
                                        <span
                                            id="bizum-receiver-phone-help"
                                            className={`label-text-alt ${showPhoneError ? "text-error" : "text-base-content/55"}`}
                                        >
                                            {showPhoneError
                                                ? "Introduce un móvil español de 9 dígitos."
                                                : "Móvil español dado de alta en Bizum."}
                                        </span>
                                    </div>
                                </div>

                                <div className="form-control gap-1">
                                    <label className="label py-0" htmlFor="bizum-receiver-notes">
                                        <span className="label-text text-xs font-semibold">
                                            Notas internas <span className="text-base-content/45">(opcional)</span>
                                        </span>
                                    </label>
                                    <textarea
                                        id="bizum-receiver-notes"
                                        name="notes"
                                        className="textarea textarea-bordered textarea-sm min-h-16"
                                        placeholder="Titular de la cuenta, motivo del cambio…"
                                        value={form.notes}
                                        disabled={saving}
                                        onChange={(e) => setField("notes", e.target.value)}
                                    />
                                </div>

                                {saveError && (
                                    <div role="alert" className="alert alert-error py-2">
                                        <AlertTriangle className="w-4 h-4" aria-hidden />
                                        <span className="text-sm">{saveError}</span>
                                    </div>
                                )}

                                <div className="flex items-center gap-3 flex-wrap">
                                    <BrandButton type="submit" size="sm" disabled={!canSave}>
                                        {saving && <span className="loading loading-spinner loading-xs" aria-hidden />}
                                        {saving ? "Publicando…" : "Publicar receptor"}
                                    </BrandButton>
                                    <span className="text-xs text-base-content/55">
                                        Configurar el receptor no cobra ni comprueba ningún pago.
                                    </span>
                                </div>
                            </form>
                        </>
                    )}
                </div>
            )}
        </section>
    );
}
